'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import React from 'react';

import {
  LocationPhases,
  SimpleIndustriousLocationSearchDto,
} from '@/lib/locations/dto';
import { WithDataTestId } from '@/types/with-data-testid';

import {
  ApplicationFormData,
  BackgroundImageProps,
  BackgroundType,
  BackgroundVideoProps,
  FormWithBackgroundData,
  FormWithBackgroundType,
  ImageEyebrowProps,
  RegularFormData,
  TextEyebrowProps,
} from '../types';
import ApplicationForm from './ApplicationForm/ApplicationForm';
import { BackgroundWrapper } from './BackgroundWrapper';
import { FormWithBackgroundVariants } from './FormWithBackgroundVariants.enum';
import FormWrapper from './shared/FormWrapper';

const LeftPanel = dynamic(() => import('./shared/LeftPanel'), { ssr: false });
const RegularForm = dynamic(() => import('./RegularForm/RegularForm'), {
  ssr: false,
});
const BrokerForm = dynamic(() => import('./BrokerForm/BrokerForm'), {
  ssr: false,
});
const BackgroundImage = dynamic(
  () => import('../shared/BackgroundImage').then((mod) => mod.BackgroundImage),
  { ssr: false }
);
const BackgroundVideo = dynamic(
  () => import('../shared/BackgroundVideo').then((mod) => mod.BackgroundVideo),
  { ssr: false }
);

export type FormWithBackgroundModuleProps = {
  type: FormWithBackgroundType;
  title?: string;
  description?: string;
  note?: string;
  eyebrow?: TextEyebrowProps | ImageEyebrowProps;
  background: BackgroundImageProps | BackgroundVideoProps;
  withBlackOverlay?: boolean;
  showPromoCode?: boolean;
  formTitle: string;
  formDescription?: string;
  isBackgroundCoverNavigationBar?: boolean;
  locations: SimpleIndustriousLocationSearchDto[];
  locationSlugs?: string[];
  promoCode?: string;
  phases?: LocationPhases[];
  variant?: FormWithBackgroundVariants;
  onSubmit: (
    form: FormWithBackgroundData,
    formType: FormWithBackgroundType
  ) => void;
  onChange: (data: RegularFormData | ApplicationFormData) => void;
} & WithDataTestId;

export function FormWithBackgroundModule({
  title,
  description,
  note,
  eyebrow,
  background,
  isBackgroundCoverNavigationBar,
  locations,
  locationSlugs,
  promoCode,
  showPromoCode,
  formTitle,
  formDescription,
  type,
  phases,
  withBlackOverlay,
  variant,
  dataTestId,
  onSubmit,
  onChange,
}: FormWithBackgroundModuleProps) {
  const [formExtended, setFormExtended] = useState(false);
  const isVertical = variant === FormWithBackgroundVariants.VerticalForm;

  const showLeftPanel = !!title || !!description || !!note || !!eyebrow;

  const handleSubmit = (form: FormWithBackgroundData) => {
    onSubmit(form, type);
  };

  return (
    <section className="flex justify-center p-5" data-testid={dataTestId}>
      <BackgroundWrapper
        isBackgroundCoverNavigationBar={isBackgroundCoverNavigationBar}
        isFormExtended={formExtended}
      >
        {background.type === BackgroundType.Image && (
          <BackgroundImage {...background.image} />
        )}
        {background.type === BackgroundType.Video && (
          <BackgroundVideo {...background.video} />
        )}
      </BackgroundWrapper>
      {withBlackOverlay && (
        <BackgroundWrapper
          isBackgroundCoverNavigationBar={isBackgroundCoverNavigationBar}
          isFormExtended={formExtended}
        >
          <div className="w-full h-full bg-[rgba(0,0,0,0.4)]" />
        </BackgroundWrapper>
      )}

      <div className="flex max-lg:flex-col max-lg:mt-3 lg:items-center lg:justify-center medium-container lg:min-h-[400px]">
        {showLeftPanel && (
          <LeftPanel
            title={title}
            description={description}
            eyebrow={eyebrow}
            note={note}
          />
        )}

        <FormWrapper
          title={formTitle}
          description={formDescription}
          extended={formExtended}
          variant={variant}
          onExtended={() => setFormExtended(true)}
        >
          {type === FormWithBackgroundType.Regular && (
            <RegularForm
              locations={locations}
              locationSlugs={locationSlugs}
              phases={phases}
              promoCode={promoCode}
              showPromoCode={showPromoCode}
              isVertical={isVertical}
              onSubmit={handleSubmit}
              onChange={onChange}
            />
          )}
          {type === FormWithBackgroundType.Application && (
            <ApplicationForm
              locations={locations}
              locationSlugs={locationSlugs}
              phases={phases}
              promoCode={promoCode}
              isVertical={isVertical}
              showPromoCode={showPromoCode}
              onSubmit={handleSubmit}
              onChange={onChange}
            />
          )}
          {type === FormWithBackgroundType.Broker && (
            <BrokerForm onSubmit={handleSubmit} isVertical={isVertical} />
          )}
        </FormWrapper>
      </div>
    </section>
  );
}

export default React.memo(FormWithBackgroundModule);
